import { Popover, PopoverContent, PopoverTrigger } from '@shared/components/ui/popover';
import { Button } from '@components/ui/button/Button';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useVirtualizer } from '@tanstack/react-virtual';
import { faBars, faFileImport, faNoteSticky, faPen, faPlus, faTrash } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { CountryModel } from '../../models/Country';
import { CoverageRuleModel } from '../../models/CoverageRule';
import { PaymentMethodModel } from '../../models/PaymentMethod';

interface CoverageRulesTableProps {
  rules: CoverageRuleModel[];
  onAdd: () => void;
  onBulkImport: () => void;
  onEdit: (rule: CoverageRuleModel) => void;
  onDelete: (rule: CoverageRuleModel) => void;
  onReorder: (newOrder: CoverageRuleModel[]) => void;
  isReordering?: boolean;
  paymentMethods: PaymentMethodModel[];
  countries: CountryModel[];
  canCreate?: boolean;
  canEdit?: boolean;
  canDelete?: boolean;
}

interface SortableRowProps {
  rule: CoverageRuleModel;
  onEdit: (rule: CoverageRuleModel) => void;
  onDelete: (rule: CoverageRuleModel) => void;
  paymentMethods: PaymentMethodModel[];
  countries: CountryModel[];
  canEdit?: boolean;
  canDelete?: boolean;
}

const SortableRow = ({
  rule,
  onEdit,
  onDelete,
  paymentMethods,
  countries,
  canEdit = true,
  canDelete = true,
}: SortableRowProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: rule.id,
    disabled: !canEdit,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const getCountryName = (countryId: number) => {
    const country = countries.find((c) => c.id === countryId);
    return country?.name || `País #${countryId}`;
  };

  const getPaymentMethodName = (paymentMethodId: number | null) => {
    if (!paymentMethodId) return null;
    const method = paymentMethods.find((m) => m.id === paymentMethodId);
    return method?.method || null;
  };

  const renderPostalCode = () => {
    const from = rule.cp_from || '';
    const to = rule.cp_to || '';
    // Ambos vacíos
    if (!from && !to) return '—';
    // Iguales (y no vacíos)
    if (from && to && from === to) return from;
    // Rango
    return `${from || '—'} → ${to || '—'}`;
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="border border-border rounded-md bg-card hover:bg-muted/30 transition-colors group"
    >
      {/* Fila principal */}
      <div className="grid grid-cols-12 gap-3 items-center px-3 py-2.5 text-sm">
        {/* Drag + Priority */}
        <div className="col-span-1 flex items-center gap-2">
          {/* 🎯 Solo mostrar drag handle si tiene permiso de edición */}
          {canEdit ? (
            <button
              className="cursor-grab active:cursor-grabbing text-muted-foreground hover:text-foreground transition-colors"
              {...attributes}
              {...listeners}
            >
              <FontAwesomeIcon icon={faBars} className="h-3.5 w-3.5" />
            </button>
          ) : (
            <div className="w-3.5" />
          )}
          <span className="font-bold text-primary">#{rule.priority}</span>
        </div>

        {/* Country */}
        <div className="col-span-2 text-foreground font-medium truncate" title={getCountryName(rule.country_id)}>
          {getCountryName(rule.country_id)}
        </div>

        {/* CP Range */}
        <div className="col-span-2 flex items-center gap-1.5 text-muted-foreground font-mono text-xs">
          <span className="text-foreground">{renderPostalCode()}</span>
        </div>

        {/* Type */}
        <div className="col-span-2">
          <span
            className={`px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${
              rule.rule === 'allow'
                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
            }`}
          >
            {rule.rule === 'allow' ? '✓ Incluir' : '✕ Denegar'}
          </span>
        </div>

        {/* Payment Method */}
        <div className="col-span-3 text-sm truncate" title={getPaymentMethodName(rule.payment_method_id) || ''}>
          {getPaymentMethodName(rule.payment_method_id) ? (
            <span className="text-foreground">{getPaymentMethodName(rule.payment_method_id)}</span>
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
        </div>

        {/* Actions */}
        <div className="col-span-2 flex gap-1 justify-end">
          {rule.notes && (
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 w-7 p-0 hover:bg-primary/10 hover:text-primary transition-colors"
                  title="Notas"
                >
                  <FontAwesomeIcon icon={faNoteSticky} className="h-3.5 w-3.5" />
                </Button>
              </PopoverTrigger>
              <PopoverContent side="left" align="end" sideOffset={6} className="w-80 p-3 text-sm">
                <div className="max-h-56 overflow-auto whitespace-pre-wrap text-foreground">{rule.notes}</div>
              </PopoverContent>
            </Popover>
          )}
          {/* 🎯 Solo mostrar editar si tiene permiso */}
          {canEdit && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onEdit(rule)}
              className="h-7 w-7 p-0 hover:bg-primary/10 hover:text-primary transition-colors"
              title="Editar"
            >
              <FontAwesomeIcon icon={faPen} className="h-3.5 w-3.5" />
            </Button>
          )}
          {/* 🎯 Solo mostrar eliminar si tiene permiso */}
          {canDelete && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => onDelete(rule)}
              className="h-7 w-7 p-0 text-destructive hover:text-destructive hover:bg-destructive/10 transition-colors"
              title="Eliminar"
            >
              <FontAwesomeIcon icon={faTrash} className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export const CoverageRulesTable = ({
  rules,
  onAdd,
  onBulkImport,
  onEdit,
  onDelete,
  onReorder,
  isReordering = false,
  paymentMethods,
  countries,
  canCreate = true,
  canEdit = true,
  canDelete = true,
}: CoverageRulesTableProps) => {
  const { t } = useTranslation('coverageZonesModule');
  const [localRules, setLocalRules] = React.useState<CoverageRuleModel[]>(rules);
  const [hasChanges, setHasChanges] = React.useState(false);
  const parentRef = React.useRef<HTMLDivElement>(null);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Virtualization configuration
  const virtualizer = useVirtualizer({
    count: localRules.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 60, // Altura estimada de cada fila en px
    overscan: 5, // Renderizar 5 elementos adicionales fuera de la vista
    measureElement:
      typeof window !== 'undefined' && navigator.userAgent.includes('Firefox')
        ? undefined
        : (element) => element?.getBoundingClientRect().height ?? 60,
  });

  // Update local rules when props change using useEffect
  React.useEffect(() => {
    setLocalRules(rules);
    setHasChanges(false);
  }, [rules]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setLocalRules((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);

        const newOrder = arrayMove(items, oldIndex, newIndex);
        // Update priorities
        const updatedOrder = newOrder.map((item, index) => ({
          ...item,
          priority: index + 1,
        }));

        setHasChanges(true);
        return updatedOrder;
      });
    }
  };

  const handleSaveOrder = () => {
    onReorder(localRules);
    setHasChanges(false);
  };

  const handleCancelReorder = () => {
    setLocalRules(rules);
    setHasChanges(false);
  };

  if (rules.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 px-4 border-2 border-dashed border-border rounded-lg bg-muted/20">
        <p className="text-sm text-muted-foreground mb-3">{t('coverageRules.noRules')}</p>
        <div className="flex gap-2">
          {canCreate && (
            <>
              <Button
                onClick={onAdd}
                variant="outline"
                size="sm"
                className="gap-2 border-2 border-dashed hover:bg-primary/5 hover:border-primary transition-colors"
              >
                <FontAwesomeIcon icon={faPlus} className="h-4 w-4" />
                {t('coverageRules.addRule')}
              </Button>
              <Button
                onClick={onBulkImport}
                variant="outline"
                size="sm"
                className="gap-2 border-2 border-dashed hover:bg-primary/5 hover:border-primary transition-colors"
              >
                <FontAwesomeIcon icon={faFileImport} className="h-4 w-4" />
                {t('bulkImport.title')}
              </Button>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Header with Add Button */}
      <div className="flex items-center justify-between">
        <span className="text-xs text-muted-foreground">
          {t('coverageRules.rulesCount', { count: localRules.length })}
        </span>
        <div className="flex gap-2">
          {hasChanges && canEdit && (
            <>
              <Button
                onClick={handleCancelReorder}
                variant="outline"
                size="sm"
                disabled={isReordering}
                className="h-7 text-xs px-3 transition-colors"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleSaveOrder}
                variant="primary"
                size="sm"
                disabled={isReordering}
                className="h-7 text-xs px-3 transition-colors"
              >
                {isReordering ? 'Guardando...' : t('coverageRules.saveOrder')}
              </Button>
            </>
          )}
          {!hasChanges && canCreate && (
            <>
              <Button
                onClick={onAdd}
                variant="outline"
                size="sm"
                className="gap-2 h-7 text-xs px-3 border-dashed hover:bg-primary/5 hover:border-primary transition-colors"
              >
                <FontAwesomeIcon icon={faPlus} className="h-3.5 w-3.5" />
                {t('coverageRules.addRule')}
              </Button>
              <Button
                onClick={onBulkImport}
                variant="outline"
                size="sm"
                className="gap-2 h-7 text-xs px-3 border-dashed hover:bg-primary/5 hover:border-primary transition-colors"
              >
                <FontAwesomeIcon icon={faFileImport} className="h-3.5 w-3.5" />
                {t('bulkImport.title')}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Table Header */}
      <div className="grid grid-cols-12 gap-3 px-3 py-2 text-xs font-semibold text-muted-foreground bg-muted/50 rounded-md border border-border">
        <div className="col-span-1"></div>
        <div className="col-span-2">País</div>
        <div className="col-span-2">Código Postal</div>
        <div className="col-span-2">Tipo</div>
        <div className="col-span-3">Método de Pago</div>
        <div className="col-span-2 text-right">Acciones</div>
      </div>

      {/* Rows with Drag & Drop and Virtualization */}
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={localRules.map((r) => r.id)} strategy={verticalListSortingStrategy}>
          <div
            ref={parentRef}
            className="overflow-auto smooth-scroll table-scrollbar-minimal"
            style={{
              height: localRules.length > 10 ? '600px' : `${Math.min(localRules.length * 60 + 20, 600)}px`,
              maxHeight: '600px',
            }}
          >
            {/* Virtual container */}
            <div
              style={{
                height: `${virtualizer.getTotalSize()}px`,
                width: '100%',
                position: 'relative',
              }}
            >
              {/* Virtual items */}
              {virtualizer.getVirtualItems().map((virtualItem) => {
                const rule = localRules[virtualItem.index];
                return (
                  <div
                    key={rule.id}
                    data-index={virtualItem.index}
                    ref={virtualizer.measureElement}
                    className="absolute top-0 left-0 w-full"
                    style={{
                      transform: `translateY(${virtualItem.start}px)`,
                    }}
                  >
                    <div className="mb-2">
                      <SortableRow
                        rule={rule}
                        onEdit={onEdit}
                        onDelete={onDelete}
                        paymentMethods={paymentMethods}
                        countries={countries}
                        canEdit={canEdit}
                        canDelete={canDelete}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </SortableContext>
      </DndContext>

      {/* Reorder Warning */}
      {hasChanges && (
        <div className="text-xs py-2 px-3 bg-yellow-50 dark:bg-yellow-900/20 rounded border border-yellow-200 dark:border-yellow-800">
          <span className="text-yellow-800 dark:text-yellow-400">
            Reordenado. Haz clic en "Guardar Orden" para aplicar.
          </span>
        </div>
      )}
    </div>
  );
};
